import type { Board } from "../board";
import { DamageType } from "../damageType";
import { Direction, directionDelta, directionFromVector, reverseDirection } from "../direction";
import { DirectionalSlash } from "../effects/directionalSlash";
import { MeleeHitMovementEffect } from "../effects/meleeHitMovementEffect";
import { MovementEffect } from "../effects/movementEffect";
import { ParticleEffect } from "../effects/particleEffect";
import { Timing } from "../effects/effect";
import { isBumpable, type Bumpable } from "../interfaces";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import type { PhysicalThing } from "../physicalThing";
import { PhysicalThingWithHp, STD_BASE_HP } from "../physicalThingWithHp";
import type { Player } from "../player";
import { ArtilleryRobot } from "../things/artilleryRobot";
import { MiningRobot } from "../things/miningRobot";
import { MultiTileThingPart } from "../things/multitile/multiTileThingPart";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { Weapon } from "../weapon";
import { WeaponVisual } from "../weaponVisual";
import { XY } from "../xy";

type MoveResult = "moved" | "bumpedNoMove" | "blocked";

// Sword attack variants. Internal names — see attackOne() for visual/sound dispatch.
//   lunge: A — moved into space, stabbed the cell directly ahead.
//   slash: B — enemy was adjacent both before and after the move (sword arc).
//   jab:   C — couldn't move (mob/robot blocking the target cell), single weak hit.
type SwordAttackType = "lunge" | "slash" | "jab";

// What the sword can hit at a given cell. `target` is what receives applyDamage
// (a Mob, or a robot's MultiTileThingPart that forwards to the core), `identity`
// is the dedup key (the robot core, or the mob itself), and `xy` is the cell
// where the visual swing/Hit indicator should land.
type SwordTarget = {
	target: PhysicalThingWithHp;
	// Dedup key only — reference identity matters, not the concrete type. For mobs it's
	// the mob itself; for big robots (MultiTileThingCore) it's the core, so two adjacent
	// parts of the same robot don't trigger two hits.
	identity: PhysicalThing;
	xy: XY;
};

export class Sword extends Weapon {
	static key = "Sword";

	constructor(owner: Player) {
		super(owner, 0, true);
	}

	override getAutoFireRange(): number {
		return 1;
	}

	override getWeaponVisual(): WeaponVisual {
		return WeaponVisual.CLEAVER;
	}

	override pullTrigger(direction: Direction, board: Board): boolean {
		const target = this.owner.xy.add(directionDelta(direction));

		if (!board.isInBounds(target)) {
			board.turnAbortedByPlayer = true;
			return false;
		}

		// Track effects & sounds we add so we can shift their animation orders below 0
		// at the end. Net effect: the player's move + every lunge/slash plays before
		// any other entity's order-0 animation — "the sword resolves first, then mobs react".
		const effectsStartIdx = board.lastTurnEffects.length;
		const mySounds: Sound[] = [];

		// Hittable thing in our target cell: a single attack on it, no movement, no slash.
		// Worst-case sword move; intentionally weak to disincentivize stab-into-mob play
		// while still letting the player out of corners they could otherwise be stuck in.
		const blocker = this.findHittable(board, target);
		if (blocker !== null) {
			this.attackOne(board, blocker.target, target, "jab", mySounds);
			board.stats.recordShotFired(Sword.key);
			this.shiftOrdersBelowZero(board, effectsStartIdx, mySounds);
			return true;
		}

		const targetsBefore = this.collectAdjacentTargets(board, this.owner.xy);

		const moveResult = this.attemptMove(board, target, direction);
		if (moveResult === "blocked") {
			board.turnAbortedByPlayer = true;
			return false;
		}
		if (moveResult === "bumpedNoMove") {
			return true;
		}

		const newXY = this.owner.xy;
		const targetsAfter = this.collectAdjacentTargets(board, newXY);
		const hitSet = new Set<PhysicalThing>();

		const lungeXY = newXY.add(directionDelta(direction));
		if (board.isInBounds(lungeXY)) {
			const lunge = this.findHittable(board, lungeXY);
			if (lunge !== null) {
				this.attackOne(board, lunge.target, lungeXY, "lunge", mySounds);
				hitSet.add(lunge.identity);
			}
		}

		for (const [identity, info] of targetsBefore) {
			if (!targetsAfter.has(identity)) continue;
			if (hitSet.has(identity)) continue;
			const after = targetsAfter.get(identity)!;
			this.attackOne(board, after.target, after.xy, "slash", mySounds);
			hitSet.add(identity);
		}

		board.stats.recordShotFired(Sword.key);
		this.shiftOrdersBelowZero(board, effectsStartIdx, mySounds);
		return true;
	}

	fire(_direction: Direction, _board: Board): void {
		// Logic lives in pullTrigger.
	}

	private attemptMove(board: Board, target: XY, direction: Direction): MoveResult {
		if (!board.isPassable(target)) {
			const bumpables = board.getThingsAt(target).filter((t) => isBumpable(t)) as unknown as Bumpable[];
			if (bumpables.length === 0) return "blocked";

			let successes = 0;
			for (const b of bumpables) {
				if (b.bump(reverseDirection(direction), this.owner)) successes++;
			}
			if (successes === 0) return "blocked";

			board.bumpActionOccurred = true;
			if (!board.isPassable(target)) return "bumpedNoMove";
		}

		const oldXY = this.owner.xy;
		this.owner.xy = target;
		board.addEffect(new MovementEffect(this.owner, oldXY, target));
		board.addEffect(new ParticleEffect(oldXY, ParticleType.STEP_DUST, 1));
		board.stats.recordTileWalked();
		return "moved";
	}

	private collectAdjacentTargets(board: Board, center: XY): Map<PhysicalThing, SwordTarget> {
		const result = new Map<PhysicalThing, SwordTarget>();
		for (const xy of center.getNeighbors()) {
			if (!board.isInBounds(xy)) continue;
			const found = this.findHittable(board, xy);
			if (found !== null && !result.has(found.identity)) {
				result.set(found.identity, found);
			}
		}
		return result;
	}

	private findHittable(board: Board, xy: XY): SwordTarget | null {
		for (const t of board.getThingsAt(xy)) {
			if (!t.active) continue;
			if (t instanceof Mob) {
				if (t.isPermanentlyFrozen()) continue;
				return { target: t, identity: t, xy };
			}
			if (t instanceof MultiTileThingPart) {
				const core = t.controller.getThing();
				if (core instanceof MiningRobot || core instanceof ArtilleryRobot) {
					return { target: t, identity: core, xy };
				}
			}
		}
		return null;
	}

	private attackOne(
		board: Board,
		target: PhysicalThingWithHp,
		targetXY: XY,
		type: SwordAttackType,
		mySounds: Sound[],
	): void {
		// Snapshot thingList so we can identify Sounds the target emits in reaction to
		// being hit (hurt sound, death sound, …). The player has PRIORITY_FIRST so no
		// other entity has processed yet — anything new on the board between this snapshot
		// and after applyDamage is attributable to this strike.
		const allThings = board.getAllThings();
		const reactionStart = allThings.length;
		target.applyDamage(DamageType.MELEE, STD_BASE_HP, this.owner);

		// MeleeHitMovementEffects on the player chain via Board.addEffect's same-thing logic;
		// sync the variant Hit indicator and sword sound to that order so each strike's
		// visuals and audio land on the same animation frame for a ninja-flurry feel.
		const meleeMove = new MeleeHitMovementEffect(this.owner, this.owner.xy, targetXY);
		board.addEffect(meleeMove);
		const order = meleeMove.getAnimationOrder();
		// `targetXY` is always adjacent to the player (lunge/slash/jab all resolve to
		// king-neighbor cells), so the delta-sign vector resolves cleanly to a Direction.
		const origin = this.owner.xy;
		const dir = directionFromVector(
			new XY(Math.sign(targetXY.x - origin.x), Math.sign(targetXY.y - origin.y)),
		) ?? Direction.E;
		const hit = new DirectionalSlash(targetXY, dir);
		hit.setAnimationOrder(order);
		board.addEffect(hit);
		const sound = new Sound(soundIdForType(type), targetXY);
		sound.setTiming(Timing.BEFORE_STATE_UPDATE);
		sound.setAnimationOrder(order);
		board.addNonPhysicalThing(sound);
		mySounds.push(sound);

		// Sync any Sounds the mob added in reaction to the hit (hurt/death) so they
		// fire on the same animation frame as the swing that caused them. `allThings` is
		// a live reference to board's thingList; new entries appended by applyDamage are
		// visible here.
		for (let i = reactionStart; i < allThings.length; i++) {
			const t = allThings[i];
			if (!(t instanceof Sound)) continue;
			t.setTiming(Timing.BEFORE_STATE_UPDATE);
			t.setAnimationOrder(order);
			mySounds.push(t);
		}
		// TODO: do the same for ParticleEffects emitted by the mob (e.g. blood bursts).
		// Today they're spawned immediately at loadTurnEffects time via spawnBurst(),
		// bypassing the animation-order queue — see gameSession.loadTurnEffects line ~706.
		// Fix would be routing ParticleEffect through UnboundedEffectQueue (or a parallel
		// queue) so setAnimationOrder takes effect, then mirror the loop above for effects
		// added to lastTurnEffects between the snapshot and the meleeMove.
	}

	// Shifts every effect added by this swing (since `startIdx`) plus the tracked sounds
	// so the player's last MovementEffect ends at order -1 — making the whole sword sequence
	// resolve before any other entity's default order-0 animation.
	//
	// No-op if no enemy was actually hit: an empty `sounds` array means the player just
	// moved (and maybe bumped a wall) without lunging or slashing — in that case we want
	// the move to animate at default order 0, indistinguishable from a normal walk.
	private shiftOrdersBelowZero(board: Board, startIdx: number, sounds: Sound[]): void {
		if (sounds.length === 0) return;
		const myEffects = board.lastTurnEffects.slice(startIdx);
		let maxPlayerMoveOrder = -Infinity;
		for (const e of myEffects) {
			if (e instanceof MovementEffect && e.thing === this.owner) {
				const o = e.getAnimationOrder();
				if (o > maxPlayerMoveOrder) maxPlayerMoveOrder = o;
			}
		}
		if (maxPlayerMoveOrder === -Infinity) return;
		const shift = -(maxPlayerMoveOrder + 1);
		if (shift >= 0) return;
		for (const e of myEffects) e.setAnimationOrder(e.getAnimationOrder() + shift);
		for (const s of sounds) s.setAnimationOrder(s.getAnimationOrder() + shift);
	}

	static deserialize(data: any, owner: Player): Sword {
		const sword = new Sword(owner);
		sword.ammo = data.ammo;
		return sword;
	}
}

function soundIdForType(type: SwordAttackType): SoundId {
	switch (type) {
		case "lunge":
			return SoundId.SWORD_LUNGE;
		case "slash":
			return SoundId.SWORD_SLASH;
		case "jab":
			return SoundId.SWORD_JAB;
	}
}
